<template>
    <div class="my-2">
        <document-disassociate-confirmation></document-disassociate-confirmation>
        <delete-confirmation name="document" @confirm="deleteDocument"></delete-confirmation>
        <div class="d-flex justify-content-between align-items-end">        
            <div><h5 class="font-weight-bold">Associated Documents</h5></div>
            <div v-if="canAssociate">
                <button type="button" class="btn btn-sm btn-primary mb-1" title="Upload Associated Document" @click="uploadDocument()">
                    <i class="fas fa-file-upload fa-lg fa-fw"></i>
                </button>
            </div>
        </div>
        <table :class="['table table-sm', {'table-striped' :documents.length}]">
            <thead>
                <tr>
                    <th class="fit">Document Type</th>
                    <th>Description</th>
                    <th class="fit">Owner</th>
                    <th class="fit">Uploaded/Received</th>
                    <th class="fit"></th>
                </tr>
            </thead>              
            <tbody>
                <tr v-if="loading">
                    <td class="text-muted text-center" colspan="5">
                        <h4><i class="fas fa-spinner fa-lg fa-spin align-middle"></i> Loading...</h4>
                    </td>                    
                </tr>
                <tr v-else-if="documents.length" v-for="document in documents" :key="'services-associated-docs-' + document.id">
                    <td class="fit">
                        <document-type-icon :document="document" :href="getDocumentUrl(document)"></document-type-icon>
                    </td>
                    <td>
                        <input v-if="document.edit" class="form-control form-control-sm" v-model="document.description" @blur="changeDescription(document)" @keyup.enter="changeDescription(document)" v-focus>
                        <span v-else @click="document.edit = true;" :title="document.description">                                
                            {{ document.description }}
                            <a href="javascript:void(0)" class="edit-button"><i class="fas fa-pencil-alt fa-xs"></i></a>
                        </span>                        
                    </td>
                    <td class="fit">{{ document.created_by }}</td>
                    <td class="fit">{{ document.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                    <td class="fit text-center">

                        <!-- dissociate document -->
                        <button  v-if="canAssociate && document.can_disassociate" class="btn btn-link px-1 py-0" title="Unlink from Document" @click="confirmDissociate(document.id)">
                            <i class="fas fa-unlink fa-md fa-fw"></i>
                        </button>

                        <!-- delete document -->
                        <button v-else-if="canAssociate && document.can_delete" class="btn btn-link px-1 py-0" title="Delete Document" @click="confirmDelete(document.id)">
                            <i class="fas fa-trash-alt fa-lg fa-fw"></i>
                        </button>

                        <i v-else class="fas fa-lg fa-fw"></i>        
                    </td>
                </tr>
                <tr v-else>
                    <td class="text-muted text-center" colspan="5">
                        <h4>No Associated Documents</h4>
                    </td>                     
                </tr>                                
            </tbody>
        </table>
    </div>
</template>

<script>
import { DocumentType } from '../../constants.js';
import DocumentDisassociateConfirmation from '../documents/DocumentDisassociateComponent.vue';
import DocumentTypeIcon from '../documents/DocumentTypeIconComponent.vue';

export default {
    components: {
        DocumentDisassociateConfirmation,
        DocumentTypeIcon
    },
    props: {
        clientId: Number,
        claimId: Number,
        documentId: Number,
        canAssociate: Boolean,
        historical: Boolean
    },
    data() {
        return {
            loading: false,
            documents: [],
            associationOptions: [],
            DocumentType: DocumentType
        }
    },
    computed: {
        href: function () {
            return this.historical ?
                `/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${this.documentId}/associations` :
                `/ajax/claims/${this.claimId}/documents/${this.documentId}/associations`;
        },
        uploadHref: function () {
            return this.historical ?
                `/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${this.documentId}/document` :
                `/ajax/claims/${this.claimId}/upload`;
        }
    },
    methods: {
        refresh: function () {

            this.loading = true;
            axios.get(this.href)
            .then(response => {

                // add edit flag to each document
                this.documents = response.data.documents.map(e => {                
                    this.$set(e, 'edit', false);
                    return e; 
                });
                
                this.associationOptions = response.data.association_options;
                this.loading = false;               
            })
            .catch(error => {
                console.log(error.response);
                this.loading = false;
            })
        },
        uploadDocument: function () {

            let defaultType = this.historical ?  DocumentType.LWC_FORM_1010_RESPONSE : DocumentType.REMITTANCE;

            // show the upload file modal
            eventHub.$emit('show-file-upload', {
                claimId: this.claimId,
                url: this.uploadHref,
                options: this.associationOptions,
                documentType: defaultType,
                associateToDocumentId: this.documentId
            });                     
        },
        changeDescription: function (document) {

            document.edit = false;

            // update the model
            this.$emit('update');

            // send the change to the server
            if (document.historical) {

                axios.patch(`/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${document.id}`, {
                    key: 'description',
                    value: document.description
                });
            }
            else {

                axios.patch(`/ajax/documents/${document.id}/description`, {
                    'description': document.description
                });
            }           
        },
        confirmDissociate: function (documentId) {

            const url = this.historical ?
                `/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${documentId}/dissociate` :
                `/ajax/documents/${documentId}/disassociate`;

            const message = 'Are you sure you want to unlink these documents?';
            eventHub.$emit('show-disassociate-confirmation', url, this.documentId, message);
        },          
        confirmDelete: function (id) {
            eventHub.$emit('show-delete-document-confirmation', id);
        },
        deleteDocument: function (id) {

            let url = this.historical ?
                `/ajax/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${id}` :
                `/ajax/documents/${id}`;

            axios.delete(url)
                .then(response => {
                    // nothing to do; server side event will cause refresh
                })
                .catch(error => {
                    let title = error.response.data.title;
                    let message = error.response.data.message;
                    let level = error.response.data.level;
                    toastr[level](message, title, {timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true});
                });            
        },
        getDocumentUrl: function (doc) {
            return doc.historical ?
                `/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${doc.id}` :
                `/claims/${this.claimId}/documents/${doc.id}`;
        }                                             
    },
    created: function () {
        this.refresh();

        // listen for claim document events
        window.Echo.private('claims.' + this.claimId)
            .listen('DocumentEvent', (e) => {
                this.refresh(); 
            });         
    },
    directives: {
        focus: {
            inserted: function (el) {
                el.focus();
            }
        }
    }     
}
</script>

<style scoped>
    .edit-button {
        position: relative;
        top: -4px;
        opacity: 0;
    }

    .edit-button:hover {
        opacity: 1;
    }
</style>